import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import TaskDetailsInfo from '@/modules/road-maintenance-module/winter-service-v2/tasks/components/TaskDetailsInfo.vue'
import { useTaskFields } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskFields'
import { useTaskTabsEdit } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskTabsEdit'
import { useTaskVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskVehicleFacts'
import {
  applyTaskForm,
  toTaskPayload,
  withTaskForm
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/taskData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  TASKS_ENDPOINT,
  TASKS_TRANSLATION_NAMESPACE as NS,
  TASK_BASIC_INFO_TAB_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

/**
 * The basic information tab of a task. It is the tab that owns the task record itself
 * - the route tab beside it talks to the street endpoints instead - so this is where
 * the fetch and the save live.
 *
 * The tab carries no action buttons of its own: the screen header sits above the tab
 * strip and drives whichever tab is open (see TaskDetailsHeader).
 */
export function useTaskDetails (options) {
  const { translate, taskId } = options || {}

  const { rows } = useTaskFields({ translate })
  const { setTabEdit } = useTaskTabsEdit()
  const { setFromTask } = useTaskVehicleFacts()

  const apiConfig = freezeObject({
    get: {
      module: WINTER_SERVICE_MODULE,
      route: `${TASKS_ENDPOINT}/${taskId}`
    },
    // Sent as POST with `_method=PATCH`, which is what the update route expects.
    post: {
      module: WINTER_SERVICE_MODULE,
      route: `${TASKS_ENDPOINT}/${taskId}`
    }
  })

  // `rows` is not just how the component finds its fields - Content.vue's
  // applyFormValuesForSend walks `tabContent[].rows[].fields[]` to build the save
  // payload, so a block that keeps its fields anywhere else saves nothing.
  const infoBlock = computed(() => ({
    component: TaskDetailsInfo,
    visible: true,
    rows: rows.value
  }))

  // The backend reports a task nested - every codebook as its own object, the vehicle's
  // whole winter service configuration under `vehicle`, the status as a flag - and the
  // fields edit it flat.
  const onDataReceived = (apiData) => {
    // What the vehicle allows has to be known before the spreading fields are drawn -
    // the material list and the quantity's range come off it, and the task carries the
    // whole configuration under `vehicle`.
    setFromTask(apiData?.data)
    applyTaskForm(apiData?.data)
  }

  const afterSave = (context, data) => {
    setFromTask(data)
    context?.refreshData?.(withTaskForm(data))
  }

  const modifyDataBeforeSend = ({ copiedData }) => toTaskPayload(copiedData)

  return computed(() => ({
    key: TASK_BASIC_INFO_TAB_KEY,
    title: translate(`${NS}.basic_information`),
    apiConfig,
    onDataReceived,
    afterSave,
    modifyDataBeforeSend,
    // Mirrored out so the header above the tab strip knows whether to offer edit or
    // save/cancel - it lives outside this tab's component tree.
    editUpdate: ({ edit }) => setTabEdit(TASK_BASIC_INFO_TAB_KEY, edit),
    tabContent: [
      infoBlock.value
    ]
  }))
}
